import { ratesLabels } from 'axl-config';
import isLoanTermCustom from '../util/isLoanTermCustom.js';
let leaseMileageOptions = [7500, 10000, 12000, 15000];
let leaseTermOptions = [24, 36, 39, 48];

/**
 * Retrieves and applies dealer-specific settings to calculate various financial
 * and lease-related parameters based on the provided inputs.
 *
 * @param {Object} params - The input parameters.
 * @param {Object} params.dealerSettings - Dealer-specific settings and configurations.
 * @param {string} [params.dealerSettings.accountStatus='INACTIVE'] - The account status of the dealer.
 * @param {number[]} [params.dealerSettings.availableLeaseTerms=[]] - Available lease terms in months.
 * @param {number[]} [params.dealerSettings.availableMilesPerYear=[]] - Available mileage options per year.
 * @param {string} [params.dealerSettings.creditScoreDefault] - Default credit score for the dealer.
 * @param {number} [params.dealerSettings.defaultFinanceTerms] - Default finance term in months.
 * @param {number} [params.dealerSettings.defaultLeaseTerms] - Default lease term in months.
 * @param {number} [params.dealerSettings.defaultMilesPerYear] - Default mileage per year for leases.
 * @param {string} [params.dealerSettings.financeCashDownMethod] - Method for calculating finance down payment ('PERCENTAGE' or 'FIXED').
 * @param {number} [params.dealerSettings.financeCashDownPct] - Percentage for finance down payment.
 * @param {number} [params.dealerSettings.financeCashDownValue] - Fixed value for finance down payment.
 * @param {number} [params.dealerSettings.leaseCashDownAmount] - Fixed value for lease down payment.
 * @param {string} [params.dealerSettings.leaseCashDownMethod] - Method for calculating lease down payment ('PERCENTAGE' or 'FIXED').
 * @param {number} [params.dealerSettings.leaseCashDownPct=10] - Percentage for lease down payment.
 * @param {boolean} [params.dealerSettings.leaseEnabled=false] - Flag indicating if leasing is enabled.
 * @param {Object} params.walletInteractions - Wallet interaction flags.
 * @param {boolean} params.walletInteractions.paymentEmphasis - Indicates if payment emphasis is applied.
 * @param {boolean} params.walletInteractions.hasUpdatedWallet - Indicates if the wallet has been updated.
 * @param {boolean} params.walletInteractions.hasUpdatedNDSCalc - Indicates if the NDS calculation has been updated.
 * @param {number} params.price - The price of the vehicle.
 * @param {Object} params.payments - Payment-related details.
 * @param {string} params.payments.creditType - The credit type for the payment.
 * @param {number} params.payments.loanTerm - The loan term in months.
 * @param {number} params.payments.downPayment - The down payment amount.
 * @param {number} params.payments.downPaymentPercent - The down payment percentage.
 * @param {number} params.payments.leaseTerm - The lease term in months.
 * @param {number} params.payments.leaseMileageRange - The mileage range for the lease.
 * @param {number} params.payments.interestRate - The interest rate for the payment.
 * @param {Object} params.nationalRates - National rates for different credit tiers.
 *
 * @returns {Object} An object containing the applied dealer settings and calculated values.
 * @returns {string} [return.creditType] - The applied credit type.
 * @returns {number|null} [return.defaultLeaseDownPayment] - The default lease down payment.
 * @returns {number|null} [return.downPayment] - The calculated down payment amount.
 * @returns {number|null} [return.downPaymentPercent] - The calculated down payment percentage.
 * @returns {number} [return.interestRate] - The calculated interest rate.
 * @returns {number} [return.localLeaseMileageRange] - The applied lease mileage range.
 * @returns {number} [return.localLeaseTerm] - The applied lease term.
 * @returns {number[]} [return.leaseMileageOptions] - The available lease mileage options.
 * @returns {number[]} [return.leaseTermOptions] - The available lease term options.
 * @returns {number} [return.selectedLoanTerm] - The selected loan term.
 */
const getAppliedDealerSettings = _ref => {
  let {
    dealerSettings,
    walletInteractions,
    price,
    payments,
    nationalRates
  } = _ref;
  const {
    accountStatus = 'INACTIVE',
    availableLeaseTerms = [],
    availableMilesPerYear = [],
    creditScoreDefault,
    defaultFinanceTerms,
    defaultLeaseTerms,
    defaultMilesPerYear,
    financeCashDownMethod,
    financeCashDownPct,
    financeCashDownValue,
    leaseCashDownAmount,
    leaseCashDownMethod,
    leaseCashDownPct = 10,
    leaseEnabled = false
  } = dealerSettings;
  const {
    loanTerm,
    downPayment,
    downPaymentPercent,
    leaseTerm,
    leaseMileageRange
  } = payments;
  const {
    hasUpdatedWallet,
    hasUpdatedNDSCalc,
    paymentEmphasis
  } = walletInteractions;
  let creditType = ratesLabels[payments.creditType]?.decodedValue;
  let localLeaseTerm = leaseTerm;
  let localLeaseMileageRange = leaseMileageRange;
  let localDownPayment = downPayment; // Default is null
  let localDownPaymentPercentage = localDownPayment && localDownPayment > 0 ? null : downPaymentPercent; // Default is 0.20 i.e 20%
  let {
    interestRate
  } = payments;
  let defaultLeaseDownPayment = null;
  let selectedLoanTerm = loanTerm;

  // if terms from previous vdp is non-standard and its not the current vdps dealer setting, update to wallet default
  if (isLoanTermCustom(Number(selectedLoanTerm), creditType) && Number(selectedLoanTerm) !== defaultFinanceTerms) {
    selectedLoanTerm = 72;
  }
  if (localDownPayment === 0) {
    localDownPaymentPercentage = null;
  }
  if (accountStatus === 'ACTIVE' && !hasUpdatedNDSCalc && !hasUpdatedWallet && !paymentEmphasis && Object.keys(dealerSettings).length) {
    // match and apply credit tier
    creditType = Object.values(ratesLabels).find(key => key.subLabel.includes(creditScoreDefault?.substring(0, 3)))?.decodedValue ?? creditType;
    if (defaultFinanceTerms) {
      selectedLoanTerm = defaultFinanceTerms;
    }

    // apply down Payment
    if (financeCashDownMethod === 'PERCENTAGE') {
      localDownPaymentPercentage = financeCashDownPct / 100;
      localDownPayment = financeCashDownPct === 0 ? 0 : null;
    } else {
      localDownPaymentPercentage = null;
      localDownPayment = financeCashDownValue; // localDownPaymentPercentage or localDownPayment either of them should be null in support to wallet dependency.
    }

    // apply interest Rate
    const tierRates = nationalRates[ratesLabels[creditType]?.decodedValue];
    interestRate = tierRates?.rates[loanTerm];
    if (leaseEnabled) {
      // set lease default miles per year and term values
      if (availableMilesPerYear.length) {
        leaseMileageOptions = availableMilesPerYear;
      }
      if (availableLeaseTerms.length) {
        leaseTermOptions = availableLeaseTerms;
      }
      if (defaultLeaseTerms) {
        localLeaseTerm = defaultLeaseTerms;
      }
      if (defaultMilesPerYear) {
        localLeaseMileageRange = defaultMilesPerYear;
      }

      // set lease default downPayemnt
      if (leaseCashDownMethod === 'FIXED') {
        defaultLeaseDownPayment = leaseCashDownAmount;
      } else if (leaseCashDownMethod === 'PERCENTAGE') {
        defaultLeaseDownPayment = Math.floor(leaseCashDownPct / 100 * price);
      }
    }
  }
  return {
    creditType,
    defaultLeaseDownPayment,
    downPayment: localDownPayment,
    downPaymentPercent: localDownPaymentPercentage,
    interestRate,
    localLeaseMileageRange,
    localLeaseTerm,
    leaseMileageOptions,
    leaseTermOptions,
    selectedLoanTerm
  };
};
export { getAppliedDealerSettings as default };
